import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Phone, MessageCircle, Menu, X, Star, ChevronDown } from "lucide-react";
import { BRAND } from "@/lib/brand";
import { buildWaLink, GENERAL_MESSAGE, serviceMessage, SERVICE_GROUPS, servicesByGroup, trackWhatsApp } from "@/lib/services";
import LangSwitcher from "@/components/LangSwitcher";
import { useI18n } from "@/lib/i18n";

const PRIMARY_NAV = [
  { to: "/services", label: "Services", hasMega: true },
  { to: "/for-families-abroad", label: "For Families Abroad" },
  { to: "/#checkups", label: "Health Checkups" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Health Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [megaOpen, setMegaOpen] = useState(false);
  const { pathname } = useLocation();
  const { t } = useI18n();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); setMegaOpen(false); }, [pathname]);

  const waHref = buildWaLink(GENERAL_MESSAGE);
  const onWaClick = (name) => trackWhatsApp(name);

  return (
    <header data-testid="site-header" className="fixed top-0 inset-x-0 z-50">
      {/* Top info bar */}
      <div className="bg-navy text-white text-[13px]">
        <div className="container-x py-2 flex items-center justify-between gap-4">
          <div className="hidden sm:flex items-center gap-3 text-white/85">
            <span>Open 24/7</span><span className="opacity-50">·</span>
            <span>Serving all of Bangalore</span>
          </div>
          <div className="flex items-center gap-2">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="font-semibold">{BRAND.rating} on Google · {BRAND.ratingCount} reviews</span>
            <span className="opacity-50 mx-1">·</span>
            <LangSwitcher className="text-white" />
          </div>
        </div>
      </div>

      {/* Main header */}
      <div className={`transition-all duration-300 ${scrolled ? "bg-white/95 backdrop-blur shadow-soft" : "bg-white"}`}>
        <div className="container-x py-3 md:py-4 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-3">
            <img src={BRAND.logo} alt="Velixa Health" className="h-11 md:h-14 w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-7">
            {PRIMARY_NAV.map((l) =>
              l.hasMega ? (
                <div key={l.to} className="relative" onMouseEnter={() => setMegaOpen(true)} onMouseLeave={() => setMegaOpen(false)}>
                  <NavLink to={l.to} data-testid="nav-services" className={({ isActive }) => `inline-flex items-center gap-1 text-[15px] font-semibold text-navy hover:text-brand transition-colors ${isActive ? "text-brand" : ""}`}>
                    {l.label} <ChevronDown className="w-4 h-4" />
                  </NavLink>
                  {megaOpen && <MegaMenu onWaClick={onWaClick} />}
                </div>
              ) : (
                <NavLink key={l.to} to={l.to} data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className={({ isActive }) => `text-[15px] font-semibold text-navy hover:text-brand transition-colors ${isActive ? "text-brand" : ""}`}>
                  {l.label}
                </NavLink>
              )
            )}
          </nav>

          <div className="flex items-center gap-2">
            <a data-testid="header-call-btn" href={`tel:${BRAND.phoneTel}`} className="hidden md:inline-flex btn-outline-navy text-[14px] py-2.5 px-4">
              <Phone className="w-4 h-4" /> Call {BRAND.phone}
            </a>
            <a data-testid="header-whatsapp-btn" href={waHref} target="_blank" rel="noopener noreferrer" onClick={() => onWaClick("Header")} className="btn-wa text-[14px] py-2.5 px-4">
              <MessageCircle className="w-4 h-4" /> <span className="hidden sm:inline">{t("cta.whatsapp_us")}</span>
            </a>
            <button data-testid="mobile-menu-toggle" aria-label="Menu" onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 rounded-full border border-navy/20 text-navy">
              {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile nav */}
      {open && (
        <div data-testid="mobile-nav" className="lg:hidden bg-white border-t border-navy/10 shadow-soft">
          <div className="container-x py-4 flex flex-col">
            {PRIMARY_NAV.map((l) => (
              <NavLink key={l.to} to={l.to} data-testid={`m-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`} className="py-3 border-b border-navy/10 last:border-0 text-navy font-semibold">
                {l.label}
              </NavLink>
            ))}
            <div className="flex gap-2 mt-4">
              <a href={`tel:${BRAND.phoneTel}`} data-testid="m-call-btn" className="flex-1 btn-outline-navy">
                <Phone className="w-4 h-4" /> Call
              </a>
              <a href={waHref} target="_blank" rel="noopener noreferrer" data-testid="m-whatsapp-btn" onClick={() => onWaClick("Mobile Nav")} className="flex-1 btn-wa">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
        </div>
      )}
    </header>
  );
}

function MegaMenu({ onWaClick }) {
  return (
    <div data-testid="services-mega-menu" className="absolute left-1/2 -translate-x-1/2 top-full pt-4 w-[920px] max-w-[90vw]">
      <div className="bg-white border border-navy/10 shadow-card rounded-2xl p-6 grid grid-cols-3 gap-6">
        {SERVICE_GROUPS.map((g) => (
          <div key={g.key}>
            <div className="text-[11px] uppercase tracking-widest text-brand font-semibold mb-3">{g.title}</div>
            <ul className="space-y-2">
              {servicesByGroup(g.key).map((s) => (
                <li key={s.slug}>
                  <a
                    data-testid={`mega-link-${s.slug}`}
                    href={buildWaLink(serviceMessage(s.name))}
                    target="_blank" rel="noopener noreferrer"
                    onClick={() => onWaClick(s.name)}
                    aria-label={`Chat on WhatsApp about ${s.name}`}
                    className="text-[14.5px] text-ink hover:text-brand font-medium"
                  >
                    {s.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  );
}
